import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { FileText, GitMerge, ListChecks, Settings as SettingsIcon } from 'lucide-react'
import { Icon, iconText } from '@/lib/icons'
import { useNavigate } from '@tanstack/react-router'
import {
  useAgentSessions,
  useAllLists,
  useAllPages,
  useAllProjects,
  useAllTasks,
  useWorkspaces,
} from '@/db/hooks'
import { useScope } from '@/state/scope'
import { useUi } from '@/state/ui'
import { ALL } from '@/kinds/workspace/schema'
import { type NavItem } from '@/lib/nav'
import { useNav } from '@/lib/useNav'
import { useFactoryEnabled } from '@/kinds/agentrun/components'
import { cx } from './ui'

/** The line icon the sidebar draws, the glyph only where there is none. */
const navGlyph = (n: NavItem): ReactNode =>
  n.Icon ? <n.Icon size={16} className="inline text-muted" aria-hidden /> : n.icon

interface Item {
  id: string
  icon: ReactNode
  label: string
  hint?: string
  run: () => void
}

/** ⌘K / Ctrl+K from anywhere: jump to a view, a task, a project, a page, a session, or a workspace. */
export const CommandPalette = () => {
  const open = useUi((s) => s.paletteOpen)
  // remount on every open so the query and cursor start fresh without effects
  return open ? <Palette /> : null
}

const Palette = () => {
  const setOpen = useUi((s) => s.setPalette)
  const setQuickAdd = useUi((s) => s.setQuickAdd)
  const navigate = useNavigate()
  const setWs = useScope((s) => s.setWs)
  const [q, setQ] = useState('')
  const [cursor, setCursor] = useState(0)
  const ref = useRef<HTMLInputElement>(null)
  const tasksQ = useAllTasks()
  const projectsQ = useAllProjects()
  const pagesQ = useAllPages()
  const listsQ = useAllLists()
  const sessions = useAgentSessions()
  const workspaces = useWorkspaces()
  const { paletteNav } = useNav()
  const factory = useFactoryEnabled()

  useEffect(() => {
    const t = setTimeout(() => ref.current?.focus(), 20)
    return () => clearTimeout(t)
  }, [])

  const items = useMemo<Item[]>(() => {
    const tasks = tasksQ ?? []
    const projects = projectsQ ?? []
    const pages = pagesQ ?? []
    const lists = listsQ ?? []
    const needle = q.trim().toLowerCase()
    const go = (to: string) => () => {
      setOpen(false)
      void navigate({ to })
    }
    const out: Item[] = []
    const wsIcon = (slug?: string) => iconText(workspaces.find((w) => w.spec.slug === slug)?.spec.icon)
    const cmds: Item[] = [
      {
        id: 'new',
        icon: '＋',
        label: 'New task',
        hint: 'N',
        run: () => {
          setOpen(false)
          setQuickAdd(true)
        },
      },
      ...paletteNav.map((n) => ({
        id: `nav-${n.key}`,
        icon: navGlyph(n),
        label: `Go to ${n.label}`,
        run: go(n.to),
      })),
      {
        id: 'nav-settings',
        icon: <SettingsIcon size={16} className="inline text-muted" aria-hidden />,
        label: 'Go to Settings',
        run: go('/settings'),
      },
      // every change from a ready pull request to prod (the Agents app's Pipeline)
      ...(factory
        ? [
            {
              id: 'nav-pipeline',
              icon: <GitMerge size={16} className="inline text-muted" aria-hidden />,
              label: 'Go to Pipeline',
              run: go('/agents/pipeline'),
            },
          ]
        : []),
      {
        id: 'focus',
        icon: '⛶',
        label: 'Toggle focus mode',
        hint: '⌘.',
        run: () => {
          setOpen(false)
          useUi.getState().toggleFocus()
        },
      },
      ...workspaces
        .filter((w) => !w.status.archived_at)
        .map((w) => ({
          id: `ws-${w.id}`,
          icon: <Icon value={w.spec.icon} fallback="◉" />,
          label: `Switch to ${w.spec.name}`,
          run: () => {
            setOpen(false)
            setWs(w.spec.slug)
            void navigate({ to: '/' })
          },
        })),
      {
        id: 'ws-all',
        icon: '✦',
        label: 'Switch to Everything',
        run: () => {
          setOpen(false)
          setWs(ALL)
          void navigate({ to: '/' })
        },
      },
    ]
    const hit = (s: string | undefined) => !!needle && (s ?? '').toLowerCase().includes(needle)
    if (!needle) return cmds.slice(0, 8)
    out.push(...cmds.filter((c) => c.label.toLowerCase().includes(needle)).slice(0, 4))
    for (const t of tasks) {
      if (t.status.state === 'done' || t.status.state === 'dropped') continue
      if (hit(t.spec.title) || hit(t.spec.notes))
        out.push({
          id: t.id,
          icon: <Icon value={t.spec.icon} fallback="○" />,
          label: t.spec.title,
          // what the result is, not its state (a task's state is not a kind of thing)
          hint: `${wsIcon(t.labels.ws)} Task`,
          run: () => {
            setOpen(false)
            void navigate({ to: '/tasks/$id', params: { id: t.id } })
          },
        })
      if (out.length > 30) break
    }
    for (const p of projects)
      if (hit(p.spec.title) || hit(p.spec.outcome))
        out.push({
          id: p.id,
          icon: <Icon value={p.spec.icon} fallback="▣" />,
          label: p.spec.title,
          hint: `${wsIcon(p.labels.ws)} Project`,
          run: () => {
            setOpen(false)
            void navigate({ to: '/projects/$id', params: { id: p.id } })
          },
        })
    for (const n of pages)
      if (!n.deleted_at && (hit(n.name) || hit(n.spec.text)))
        out.push({
          id: n.id,
          icon: (
            <Icon
              value={n.spec.icon}
              fallback={<FileText size={16} className="inline text-muted" aria-hidden />}
            />
          ),
          label: n.name,
          hint: `${wsIcon(n.labels.ws)} Page`,
          run: () => {
            setOpen(false)
            void navigate({ to: '/pages/$id', params: { id: n.id } })
          },
        })
    for (const l of lists)
      if (!l.deleted_at && (hit(l.spec.title) || l.spec.items.some((i) => hit(i.text))))
        out.push({
          id: l.id,
          icon: (
            <Icon
              value={l.spec.icon}
              fallback={<ListChecks size={16} className="inline text-muted" aria-hidden />}
            />
          ),
          label: l.spec.title,
          hint: `${wsIcon(l.labels.ws)} List`,
          run: () => {
            setOpen(false)
            void navigate({ to: '/lists/$id', params: { id: l.id } })
          },
        })
    for (const s of sessions)
      if (hit(s.spec.title))
        out.push({
          id: s.id,
          icon: '✨',
          label: s.spec.title,
          hint: 'Chat',
          run: () => {
            setOpen(false)
            void navigate({ to: '/agents/$id', params: { id: s.id } })
          },
        })
    return out.slice(0, 40)
  }, [
    q,
    paletteNav,
    factory,
    tasksQ,
    projectsQ,
    pagesQ,
    listsQ,
    sessions,
    workspaces,
    navigate,
    setOpen,
    setQuickAdd,
    setWs,
  ])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-3"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-lg rounded-xl bg-panel border border-line shadow-2xl overflow-hidden fade-in">
        <input
          ref={ref}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setCursor(0)
          }}
          placeholder="Search everything, or type a command…"
          className="w-full rounded-none border-0 border-b border-line px-4 py-3 text-base bg-transparent"
          aria-label="Search"
          data-testid="palette-input"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setCursor((c) => Math.min(items.length - 1, c + 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setCursor((c) => Math.max(0, c - 1))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              items[cursor]?.run()
            } else if (e.key === 'Escape') setOpen(false)
          }}
        />
        <ul className="max-h-[50vh] overflow-y-auto py-1" data-testid="palette-results">
          {items.length === 0 && <li className="px-4 py-3 text-sm text-faint">No matches.</li>}
          {items.map((it, i) => (
            <li key={it.id}>
              <button
                type="button"
                onMouseEnter={() => setCursor(i)}
                onClick={it.run}
                className={cx(
                  'w-full flex items-center gap-3 px-4 min-h-10 text-left text-sm',
                  i === cursor ? 'bg-accent-soft text-fg' : 'text-fg',
                )}
              >
                <span className="w-6 text-center">{it.icon}</span>
                <span className="flex-1 truncate">{it.label}</span>
                {it.hint && <span className="text-xs text-faint">{it.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
        {/* the keys as separate hints set apart by space, never a dotted line */}
        <div className="px-4 py-1.5 text-[11px] text-faint border-t border-line hidden md:flex flex-wrap gap-x-4">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> move
          </span>
          <span>
            <kbd>Enter</kbd> open
          </span>
          <span>
            <kbd>Esc</kbd> close
          </span>
          <span>
            <kbd>⌘K</kbd> anywhere
          </span>
        </div>
      </div>
    </div>
  )
}
